import { MAX_ELAPSED_MS, MAX_SAMPLES, validateSamples, type Sample } from "./index";

export type ImportCell = string | number | null;
export type ImportTable = { name: string; rows: ImportCell[][] };
export type CurveImportMapping = {
  /** Zero-based row and column indexes into the original table. */
  headerRow: number;
  timeColumn: number | null;
  beanColumn: number | null;
  environmentColumn: number | null;
  eventColumn: number | null;
  temperatureUnit: "C" | "F" | null;
  timeFormat: "seconds" | "milliseconds" | "minutes" | "clock" | "excel" | null;
};

const MAX_TABLE_ROWS = 20_100;
const MAX_COLUMNS = 100;
const MAX_CELL_LENGTH = 10_000;
const DECIMAL = /^-?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;

function empty(cell: ImportCell | undefined): boolean {
  return cell === null || cell === undefined || (typeof cell === "string" && !cell.trim());
}

function cellError(row: number, column: number, message: string): Error {
  return new Error(`Row ${row + 1}, column ${column + 1}: ${message}`);
}

/** Sniff separators outside quoted fields; metadata lines need not match the data width. */
function delimiterFor(text: string): string {
  const delimiters = [",", ";", "\t"];
  const frequencies = delimiters.map(() => new Map<number, number>());
  let counts = [0, 0, 0];
  let quoted = false;
  let lines = 0;
  const record = () => {
    counts.forEach((count, index) => {
      if (count) frequencies[index]!.set(count, (frequencies[index]!.get(count) ?? 0) + 1);
    });
    counts = [0, 0, 0];
    lines++;
  };
  for (let index = 0; index < text.length && lines < 100; index++) {
    const character = text[index]!;
    if (character === '"') {
      if (quoted && text[index + 1] === '"') index++;
      else quoted = !quoted;
    } else if (!quoted) {
      const delimiterIndex = delimiters.indexOf(character);
      if (delimiterIndex >= 0) counts[delimiterIndex] = counts[delimiterIndex]! + 1;
      if (character === "\n" || character === "\r") {
        record();
        if (character === "\r" && text[index + 1] === "\n") index++;
      }
    }
  }
  if (lines < 100) record();
  const scores = frequencies.map(frequency => Math.max(0, ...[...frequency].map(([width, count]) => count * 100 + Math.min(width, 99))));
  return delimiters[scores.indexOf(Math.max(...scores))]!;
}

/** CSV/TSV values stay as text until the user has confirmed columns and units. */
export function parseImportCsv(input: string): ImportCell[][] {
  if (input.length > 2_000_000) throw new Error("CSV must be smaller than 2 MB.");
  const text = input.replace(/^\uFEFF/, "");
  if (!text.trim()) throw new Error("The file is empty. Choose a file containing curve readings.");
  const delimiter = delimiterFor(text);
  const rows: ImportCell[][] = [];
  let row: ImportCell[] = [];
  let field = "";
  let state: "plain" | "quoted" | "closed" = "plain";
  const addField = () => {
    if (row.length >= MAX_COLUMNS) throw cellError(rows.length, row.length, `a table can have at most ${MAX_COLUMNS} columns.`);
    row.push(field.trim() || null);
    field = "";
    state = "plain";
  };
  const addRow = () => {
    addField();
    if (rows.length >= MAX_TABLE_ROWS) throw new Error(`A table can have at most ${MAX_TABLE_ROWS.toLocaleString("en-US")} rows, including headers.`);
    rows.push(row);
    row = [];
  };
  for (let index = 0; index < text.length; index++) {
    const character = text[index]!;
    if (state === "quoted") {
      if (character === '"') {
        if (text[index + 1] === '"') { field += '"'; index++; }
        else state = "closed";
      } else field += character;
    } else if (character === delimiter) addField();
    else if (character === "\n" || character === "\r") {
      addRow();
      if (character === "\r" && text[index + 1] === "\n") index++;
    } else if (state === "closed") {
      if (character !== " " && character !== "\t") throw cellError(rows.length, row.length, "unexpected text after a closing quote.");
    } else if (character === '"') {
      if (field.trim()) throw cellError(rows.length, row.length, "a quoted value must start with a quote; escape quotes by doubling them.");
      field = "";
      state = "quoted";
    } else field += character;
    if (field.length > MAX_CELL_LENGTH) throw cellError(rows.length, row.length, `a cell can have at most ${MAX_CELL_LENGTH.toLocaleString("en-US")} characters.`);
  }
  if (state === "quoted") throw cellError(rows.length, row.length, "missing closing quote.");
  if (field || row.length || state === "closed") addRow();
  return rows;
}

function headerText(cell: ImportCell | undefined): string {
  return typeof cell === "string" ? cell.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim() : "";
}

function headerUnit(cell: ImportCell | undefined): "C" | "F" | null {
  const text = headerText(cell);
  if (/(?:^| )(?:c|celsius|centigrade)$/.test(text)) return "C";
  if (/(?:^| )(?:f|fahrenheit)$/.test(text)) return "F";
  return null;
}

function headerKind(cell: ImportCell | undefined): "time" | "bean" | "environment" | "event" | null {
  const text = headerText(cell);
  const withoutUnit = text.replace(/ (?:c|f|celsius|centigrade|fahrenheit)$/, "");
  if (["bt", "bean", "bean temp", "bean temperature"].includes(withoutUnit)) return "bean";
  if (["et", "environment", "environment temp", "environment temperature", "environmental temperature"].includes(withoutUnit)) return "environment";
  if (["event", "events", "event name", "annotation", "annotations"].includes(text)) return "event";
  if (/^(?:time|elapsed|elapsed time)(?: (?:s|sec|secs|second|seconds|ms|millisecond|milliseconds|min|mins|minute|minutes|mm ss|hh mm ss))?$/.test(text) || ["seconds", "milliseconds", "minutes"].includes(text)) return "time";
  return null;
}

function headerTimeFormat(cell: ImportCell | undefined): CurveImportMapping["timeFormat"] {
  const text = headerText(cell);
  if (/(?:^| )(?:ms|millisecond|milliseconds)$/.test(text)) return "milliseconds";
  if (/(?:^| )(?:s|sec|secs|second|seconds)$/.test(text)) return "seconds";
  if (/(?:^| )(?:min|mins|minute|minutes)$/.test(text)) return "minutes";
  if (/(?:^| )(?:mm ss|hh mm ss)$/.test(text)) return "clock";
  return null;
}

/** Only unambiguous labels are suggested. Numeric values never imply a temperature unit. */
export function suggestImportMapping(rows: ImportCell[][], selectedHeaderRow?: number): CurveImportMapping {
  if (selectedHeaderRow !== undefined && (!Number.isInteger(selectedHeaderRow) || selectedHeaderRow < 0 || selectedHeaderRow >= rows.length)) {
    throw new Error("Choose the row containing the column headers.");
  }
  let best: CurveImportMapping = {headerRow: selectedHeaderRow ?? 0, timeColumn: null, beanColumn: null, environmentColumn: null, eventColumn: null, temperatureUnit: null, timeFormat: null};
  let bestScore = 0;
  const candidates: [number, ImportCell[]][] = selectedHeaderRow === undefined
    ? rows.slice(0, 100).map((row, index) => [index, row])
    : [[selectedHeaderRow, rows[selectedHeaderRow]!]];
  candidates.forEach(([headerRow, row]) => {
    const columns = {time: [] as number[], bean: [] as number[], environment: [] as number[], event: [] as number[]};
    row.forEach((cell, column) => {
      const kind = headerKind(cell);
      if (kind) columns[kind].push(column);
    });
    const unique = (kind: keyof typeof columns) => columns[kind].length === 1 ? columns[kind][0]! : null;
    const timeColumn = unique("time");
    const beanColumn = unique("bean");
    const environmentColumn = unique("environment");
    const eventColumn = unique("event");
    const score = (timeColumn !== null ? 4 : 0) + (beanColumn !== null ? 4 : 0) + (environmentColumn !== null ? 1 : 0) + (eventColumn !== null ? 1 : 0);
    if (score <= bestScore) return;
    const units = [beanColumn, environmentColumn].filter(column => column !== null).map(column => headerUnit(row[column]));
    const temperatureUnit = units.length && units.every(unit => unit !== null && unit === units[0]) ? units[0]! : null;
    best = {headerRow, timeColumn, beanColumn, environmentColumn, eventColumn, temperatureUnit,
      timeFormat: timeColumn === null ? null : headerTimeFormat(row[timeColumn])};
    bestScore = score;
  });
  return best;
}

function numeric(cell: ImportCell | undefined, row: number, column: number): number {
  if (empty(cell)) throw cellError(row, column, "a number is required.");
  if (typeof cell !== "number" && (typeof cell !== "string" || !DECIMAL.test(cell.trim()))) {
    throw cellError(row, column, "use a number, without formulas or unit labels.");
  }
  const value = typeof cell === "number" ? cell : Number(cell);
  if (!Number.isFinite(value)) throw cellError(row, column, "use a finite number.");
  return value;
}

function elapsedTime(cell: ImportCell | undefined, format: NonNullable<CurveImportMapping["timeFormat"]>, row: number, column: number): number {
  if (empty(cell)) throw cellError(row, column, "elapsed time is missing. Only entirely blank rows can be skipped.");
  let milliseconds: number;
  if (format === "clock") {
    const text = typeof cell === "string" ? cell.trim() : "";
    if (!/^\d+:\d{2}(?:\.\d+)?$/.test(text) && !/^\d+:\d{2}:\d{2}(?:\.\d+)?$/.test(text)) {
      throw cellError(row, column, "use mm:ss or hh:mm:ss, with optional fractional seconds.");
    }
    const parts = text.split(":").map(Number);
    const seconds = parts[parts.length - 1]!;
    const minutes = parts[parts.length - 2]!;
    if (seconds >= 60 || (parts.length === 3 && minutes >= 60)) throw cellError(row, column, "clock seconds and hour-format minutes must be below 60.");
    milliseconds = (seconds + minutes * 60 + (parts.length === 3 ? parts[0]! * 3600 : 0)) * 1000;
  } else {
    const factors = {seconds: 1000, milliseconds: 1, minutes: 60_000, excel: 86_400_000};
    milliseconds = numeric(cell, row, column) * factors[format];
  }
  if (!Number.isFinite(milliseconds) || milliseconds < 0 || milliseconds > MAX_ELAPSED_MS) {
    throw cellError(row, column, "elapsed time must be between zero and four hours. Check the selected time format.");
  }
  // Excel day fractions have floating-point noise; retain sub-millisecond data to a microsecond.
  return Math.round(milliseconds * 1000) / 1000;
}

export function normalizeImportTable(rows: ImportCell[][], mapping: CurveImportMapping): { samples: Sample[]; events: { elapsedMs: number; text: string }[] } {
  if (rows.length > MAX_TABLE_ROWS) throw new Error(`A table can have at most ${MAX_TABLE_ROWS.toLocaleString("en-US")} rows, including headers.`);
  if (!Number.isInteger(mapping.headerRow) || mapping.headerRow < 0 || mapping.headerRow >= rows.length) throw new Error("Choose the row containing the column headers.");
  if (mapping.timeColumn === null || mapping.beanColumn === null) throw new Error("Choose an elapsed-time column and a bean-temperature column. Summary-only exports do not contain a roast curve.");
  if (mapping.temperatureUnit !== "C" && mapping.temperatureUnit !== "F") throw new Error("Choose Celsius (°C) or Fahrenheit (°F) for the source temperatures.");
  if (!mapping.timeFormat || !["seconds", "milliseconds", "minutes", "clock", "excel"].includes(mapping.timeFormat)) throw new Error("Choose the source time format; numeric times do not identify their units.");
  const header = rows[mapping.headerRow]!;
  const selected = [mapping.timeColumn, mapping.beanColumn, mapping.environmentColumn, mapping.eventColumn].filter(column => column !== null);
  if (selected.some(column => !Number.isInteger(column) || column < 0 || column >= header.length)) throw new Error("Each selected column must exist in the header row.");
  if (new Set(selected).size !== selected.length) throw new Error("Choose a different column for elapsed time, bean temperature, environment temperature, and events.");
  for (const column of selected) {
    if (typeof header[column] !== "string" || !/\p{L}/u.test(header[column] as string) || DECIMAL.test((header[column] as string).trim())) {
      throw cellError(mapping.headerRow, column, "choose a header row with text labels, not a row of readings.");
    }
  }
  rows.forEach((row, rowIndex) => {
    if (row.length > MAX_COLUMNS) throw cellError(rowIndex, MAX_COLUMNS, `a table can have at most ${MAX_COLUMNS} columns.`);
    row.forEach((cell, column) => {
      if (typeof cell === "string" && cell.length > MAX_CELL_LENGTH) throw cellError(rowIndex, column, `a cell can have at most ${MAX_CELL_LENGTH.toLocaleString("en-US")} characters.`);
      if (cell !== null && typeof cell !== "string" && typeof cell !== "number") throw cellError(rowIndex, column, "unsupported cell value; use text or numbers.");
    });
  });
  for (const column of [mapping.beanColumn, mapping.environmentColumn]) {
    if (column === null) continue;
    const unit = headerUnit(header[column]);
    if (unit && unit !== mapping.temperatureUnit) throw cellError(mapping.headerRow, column, `the header specifies °${unit}, but °${mapping.temperatureUnit} is selected. Temperature columns must use the same source unit.`);
  }
  const timeUnit = headerTimeFormat(header[mapping.timeColumn]);
  // Excel can display numeric day fractions as mm:ss; that header describes the
  // display, while the user explicitly chooses the stored duration representation.
  if (timeUnit && timeUnit !== mapping.timeFormat && !(timeUnit === "clock" && mapping.timeFormat === "excel")) {
    throw cellError(mapping.headerRow, mapping.timeColumn, `the header specifies ${timeUnit}, but ${mapping.timeFormat} is selected.`);
  }
  const samples: Sample[] = [];
  const events: { elapsedMs: number; text: string }[] = [];
  let previous = -1;
  for (let rowIndex = mapping.headerRow + 1; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex]!;
    if (row.every(empty)) continue;
    if (samples.length >= MAX_SAMPLES) throw cellError(rowIndex, mapping.timeColumn, `a curve can have at most ${MAX_SAMPLES.toLocaleString("en-US")} samples.`);
    const elapsedMs = elapsedTime(row[mapping.timeColumn], mapping.timeFormat, rowIndex, mapping.timeColumn);
    if (elapsedMs <= previous) throw cellError(rowIndex, mapping.timeColumn, "times must increase strictly. Remove duplicate times or correct the row order; rows are never sorted automatically.");
    const readTemperature = (column: number | null): number | null => {
      if (column === null || empty(row[column])) return null;
      const source = numeric(row[column], rowIndex, column);
      const celsius = mapping.temperatureUnit === "F" ? (source - 32) * 5 / 9 : source;
      if (celsius < -100 || celsius > 1000) throw cellError(rowIndex, column, "temperature must convert to between −100 and 1,000 °C. Check the source unit.");
      return celsius;
    };
    samples.push({elapsedMs, beanC: readTemperature(mapping.beanColumn), environmentC: readTemperature(mapping.environmentColumn)});
    if (mapping.eventColumn !== null && !empty(row[mapping.eventColumn])) {
      const cell = row[mapping.eventColumn]!;
      if (typeof cell !== "string" || /^[=+@]/.test(cell.trim())) throw cellError(rowIndex, mapping.eventColumn, "an event must be text, without a formula.");
      if (cell.trim().length > 500) throw cellError(rowIndex, mapping.eventColumn, "an event can have at most 500 characters.");
      if (events.length >= 500) throw cellError(rowIndex, mapping.eventColumn, "a curve can have at most 500 events.");
      events.push({elapsedMs, text: cell.trim()});
    }
    previous = elapsedMs;
  }
  validateSamples(samples);
  return {samples, events};
}
